import { Immutable } from "immer";
import React from "react";
import { useLazyBackground } from "tsi-common-react/src/utils/lazyBackground";
import { t } from "ttag";

import type { FourByOneImageBlock as FourByOneImageBlockValue } from "@reactivated";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

/**
 * tempurpedic ``reviews_module`` (``FourByOneImageBlock``) -- the homepage
 * reviews teaser grid (#33588 HomePage ``modules`` consolidation).
 *
 * REFACTOR (legacy ``four-by-one-image-block.html`` +
 * ``quote-image-block.html``): the four left quote tiles + one right tile,
 * wrapped in a single ``<a>`` to the reviews page, plus the brand-tracker
 * footnote. Images keep the ``.lazy-background`` / ``data-background-image``
 * CSS-background lazy-load hook (NOT the eager ``<img>`` convention -- this
 * block paints images as CSS backgrounds).
 *
 * The hover reveal (overlay opacity + image blur) is owned HERE via
 * ``useHoverReveal``, because this island owns the tile DOM. The overlay only
 * exists on tiles that have a quote, so the reveal is applied only to those
 * tiles.
 *
 * ``reviews_url`` is the legacy ``{% slugurl 'reviews' %}`` -- page-tree
 * dependent, so it is threaded in via the ``get_toolbox_block_ssr``
 * side-channel keyed by block.id (it cannot be derived from the block value).
 */
type IProps = Immutable<{
    value: FourByOneImageBlockValue;
    reviews_url: string;
}>;

type IQuoteTile = NonNullable<IProps["value"]["quote_left_1"]>;

/**
 * Hover-driven overlay reveal for a single tile: opacity on the overlay + blur
 * on the background image. `loaded` flips true 500ms after mount to defer
 * enabling the CSS opacity transition (the `--loaded` class) -- this prevents
 * an initial flash of the overlay text before it settles to opacity:0 while the
 * browser first applies the styles.
 */
const useHoverReveal = () => {
    const [hovered, setHovered] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setLoaded(true);
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, []);
    const handlers = {
        onMouseEnter: () => {
            setHovered(true);
        },
        onMouseLeave: () => {
            setHovered(false);
        },
    };
    return { hovered, loaded, handlers };
};

const imageStyle = (hasHover: boolean, hovered: boolean) =>
    hasHover ? { filter: hovered ? "blur(5px)" : "blur(0px)" } : undefined;

const hoverOverlayClassName = (loaded: boolean) =>
    concatClassNames([
        styles.fourByOneImageBlockHover,
        loaded ? styles.fourByOneImageBlockHoverLoaded : null,
    ]);

/** Reproduces ``quote-image-block.html`` (the four left quarter tiles). */
const QuoteTile = (
    props: Immutable<{ tile: IQuoteTile | null | undefined }>,
): React.JSX.Element | null => {
    const { hovered, loaded, handlers } = useHoverReveal();
    // Defer this tile's CSS background image until it scrolls into view (eager
    // under puppeteer/SSR) via the shared per-element hook. The ref binds to
    // whichever of the two lazy-background branches below actually renders.
    const imageRef = React.useRef<HTMLDivElement | null>(null);
    useLazyBackground(imageRef);
    const tile = props.tile;
    if (!tile) {
        return null;
    }
    const hasHover = !!tile.quote;
    const bgUrl = tile.image
        ? (tile.image.renditions["width-315"]?.url ?? tile.image.url)
        : "";
    return (
        <div
            className={styles.fourByOneImageBlockContainerQuarter}
            {...(hasHover ? handlers : {})}
        >
            {tile.image_description ? (
                <div
                    ref={imageRef}
                    className={concatClassNames([
                        styles.fourByOneImageBlockImage,
                        "lazy-background",
                    ])}
                    data-background-image={bgUrl}
                    title={tile.image_description}
                    style={imageStyle(hasHover, hovered)}
                >
                    <span
                        className={styles.fourByOneImageBlockImageDescription}
                    >
                        {tile.image_description}
                    </span>
                </div>
            ) : (
                <div
                    ref={imageRef}
                    className={concatClassNames([
                        styles.fourByOneImageBlockImage,
                        "lazy-background",
                    ])}
                    data-background-image={bgUrl}
                    style={imageStyle(hasHover, hovered)}
                ></div>
            )}
            {tile.quote ? (
                <div
                    className={hoverOverlayClassName(loaded)}
                    style={{ opacity: hovered ? 1 : 0 }}
                >
                    <h3 className={styles.fourByOneImageBlockHoverQuote}>
                        &ldquo;{tile.quote}&rdquo;
                    </h3>
                    <h4 className={styles.fourByOneImageBlockHoverAttribution}>
                        {tile.attribution}
                    </h4>
                </div>
            ) : null}
        </div>
    );
};

export const FourByOneImageBlock = (props: IProps): React.JSX.Element => {
    const value = props.value;
    const right = value.quote_right_1;
    const rightHasHover = !!right?.quote;
    const rightBgUrl = right?.image
        ? (right.image.renditions["width-630"]?.url ?? right.image.url)
        : "";
    const rightReveal = useHoverReveal();
    // Right-hand tile: its own ref + hook for the single lazy-background image.
    const rightImageRef = React.useRef<HTMLDivElement | null>(null);
    useLazyBackground(rightImageRef);
    return (
        <a
            href={props.reviews_url}
            className={concatClassNames([
                styles.fourByOneImageBlock,
                "l-capped-width",
                "u-margin-bottom--xl",
            ])}
        >
            <div className={styles.fourByOneImageBlockContainer}>
                <QuoteTile tile={value.quote_left_1} />
                <QuoteTile tile={value.quote_left_2} />
                <QuoteTile tile={value.quote_left_3} />
                <QuoteTile tile={value.quote_left_4} />
            </div>
            <div className={styles.fourByOneImageBlockContainer}>
                <article
                    className={styles.fourByOneImageBlockContainerHalf}
                    {...(rightHasHover ? rightReveal.handlers : {})}
                >
                    <div
                        ref={rightImageRef}
                        className={concatClassNames([
                            styles.fourByOneImageBlockImage,
                            "lazy-background",
                        ])}
                        data-background-image={rightBgUrl}
                        style={imageStyle(rightHasHover, rightReveal.hovered)}
                    ></div>
                    {right?.quote ? (
                        <div
                            className={hoverOverlayClassName(
                                rightReveal.loaded,
                            )}
                            style={{ opacity: rightReveal.hovered ? 1 : 0 }}
                        >
                            <h3
                                className={styles.fourByOneImageBlockHoverQuote}
                            >
                                &ldquo;{right.quote}&rdquo;
                            </h3>
                            <h4
                                className={
                                    styles.fourByOneImageBlockHoverAttribution
                                }
                            >
                                {right.attribution}
                            </h4>
                        </div>
                    ) : null}
                </article>
            </div>

            <div className={styles.fourByOneImageBlockFootnote}>
                &Dagger;{" "}
                {richT(
                    t`In the 2017 Tempur Sealy Brand Tracker Report, 95% of people responded that they would recommend their Tempur-Pedic<sup>®</sup> mattress`,
                )}
            </div>
        </a>
    );
};
